"use client";

import { useEffect, useState } from "react";

import { Button } from "@/components/ds/button";
import { Modal } from "@/components/ds/modal";
import type { DeviceDto } from "@/lib/api/types";
import type { DeviceDisableMode, DeviceDisableState } from "@/lib/devices/device-disable-state";

export type DeviceDisableModalProps = {
  device: DeviceDto | null;
  currentState: DeviceDisableState | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (mode: DeviceDisableMode | null) => Promise<void> | void;
  pending?: boolean;
};

export function DeviceDisableModal({
  device,
  currentState,
  open,
  onClose,
  onConfirm,
  pending = false,
}: DeviceDisableModalProps) {
  const [mode, setMode] = useState<DeviceDisableMode>("ingestion");

  useEffect(() => {
    if (open) {
      setMode(currentState?.mode ?? "ingestion");
    }
  }, [currentState?.mode, open]);

  if (!device) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={currentState ? `Manage ${device.name?.trim() || device.device_uid}` : `Disable ${device.name?.trim() || device.device_uid}`}
      description={
        currentState
          ? "Re-enable the device or update how it is disabled going forward."
          : "Choose whether to stop ingestion entirely or only suppress visualization after this point."
      }
      size="lg"
      footer={
        <div className="flex justify-end gap-ds-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          {currentState ? (
            <Button type="button" variant="secondary" onClick={() => void onConfirm(null)} disabled={pending}>
              {pending ? "Applying..." : "Enable device"}
            </Button>
          ) : null}
          <Button type="button" variant="danger" onClick={() => void onConfirm(mode)} disabled={pending}>
            {pending ? "Applying..." : currentState ? "Update disable" : "Disable"}
          </Button>
        </div>
      }
    >
      <div className="space-y-ds-4">
        {currentState ? (
          <div className="rounded-lg border border-warning/25 bg-warning/10 px-ds-3 py-ds-3 text-caption text-warning">
            Current mode: <span className="font-semibold">{currentState.mode}</span>
          </div>
        ) : null}

        <label className="flex items-start gap-ds-3 rounded-lg border border-border/35 bg-muted-bg/15 px-ds-3 py-ds-3">
          <input
            type="radio"
            className="mt-0.5 h-4 w-4 border-border text-primary focus:ring-primary/20"
            name="disable-mode"
            checked={mode === "ingestion"}
            onChange={() => setMode("ingestion")}
          />
          <span className="min-w-0">
            <span className="block text-caption font-medium text-foreground">Disable ingestion</span>
            <span className="block text-micro text-muted">
              Stops telemetry intake, live updates, and downstream alert generation for this device.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-ds-3 rounded-lg border border-border/35 bg-muted-bg/15 px-ds-3 py-ds-3">
          <input
            type="radio"
            className="mt-0.5 h-4 w-4 border-border text-primary focus:ring-primary/20"
            name="disable-mode"
            checked={mode === "visualization"}
            onChange={() => setMode("visualization")}
          />
          <span className="min-w-0">
            <span className="block text-caption font-medium text-foreground">Disable visualization</span>
            <span className="block text-micro text-muted">
              Keeps ingestion active, but hides live visualization after the disable timestamp on Live,
              Explorer, and Analytics. Historical data before that point remains available.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
